import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '@/config/api';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Activity, ArrowRight, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getPatientProfile } from '@/utils/patientProfile';
import BackToApp from '@/components/BackToApp';
import MetaTags from '@/components/SEO/MetaTags';
import { RARE_CANCERS_DATA } from '@/data/rareCancersData';
import { catalogLists } from '@/utils/catalogPayload';
import '@/components/care-page.css';

const API = API_BASE;

const REGIONS = [
  'USA',
  'Canada',
  'UK',
  'France',
  'Germany',
  'Japan',
  'Singapore',
  'China',
  'India',
  'Europe',
];

function regionFromProfile() {
  const country = (getPatientProfile()?.country || '').trim().toLowerCase();
  if (!country) return 'USA';
  if (['united states', 'usa', 'us', 'united states of america'].includes(country)) return 'USA';
  if (country === 'canada' || country === 'ca') return 'Canada';
  if (['uk', 'united kingdom', 'england', 'scotland', 'wales', 'gb'].includes(country)) return 'UK';
  if (country === 'france' || country === 'fr') return 'France';
  if (country === 'germany' || country === 'de') return 'Germany';
  if (country === 'japan' || country === 'jp') return 'Japan';
  if (country === 'singapore' || country === 'sg') return 'Singapore';
  if (country === 'china' || country === 'cn' || country === 'prc') return 'China';
  if (country === 'india' || country === 'in') return 'India';
  if (['europe', 'italy', 'spain', 'netherlands', 'sweden', 'norway', 'switzerland'].includes(country)) {
    return 'Europe';
  }
  return 'USA';
}

const CARD_TONES = {
  'ultra-rare': { fallbackType: 'Ultra-Rare' },
  'very-rare': { fallbackType: 'Very Rare' },
  rare: { fallbackType: 'Rare' },
  common: { fallbackType: 'Common' },
};
const TAG_CLASS = 'bg-cream-paper text-forest-ink border border-hair text-xs font-medium px-2.5 py-1 leading-tight';

function TrialsSection({ detail }) {
  if (!detail) return null;
  if (detail.clinical_trials_status === 'not_integrated' || !detail.clinical_trials_status) {
    return (
      <section>
        <h3 className="text-sm font-semibold text-charcoal mb-2">Clinical trials</h3>
        <div className="rounded-2xl border border-hair bg-mist p-3 sm:p-4 text-sm text-forest-ink leading-relaxed">
          Trial matching is not yet available in this product. For several of these
          cancers a clinical trial is the standard of care, ask your oncologist
          directly, and search CTRI (India) or ClinicalTrials.gov.
        </div>
      </section>
    );
  }
  const trials = detail.clinical_trials || [];
  return (
    <section>
      <h3 className="text-sm font-semibold text-charcoal mb-2">Clinical trials</h3>
      <ul className="space-y-2">
        {trials.map((t) => (
          <li key={t.id || t.title} className="text-sm text-forest-ink">{t.title || t.name}</li>
        ))}
      </ul>
    </section>
  );
}

function CenterList({ centers }) {
  if (!centers?.length) {
    return (
      <p className="text-sm text-charcoal">
        Ask your oncologist for referral to a centre with a dedicated service.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {centers.map((c) => (
        <div key={c.id || c.name} className="rounded-2xl border border-hair bg-paper p-3">
          <p className="font-semibold text-forest-ink">{c.name}</p>
          <p className="text-sm text-charcoal">{[c.city, c.country].filter(Boolean).join(', ')}</p>
          {c.designation && (
            <Badge className={`mt-2 ${TAG_CLASS}`}>{c.designation}</Badge>
          )}
          {c.match_reason && (
            <p className="text-xs text-forest-ink mt-2">{c.match_reason}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function SpecialistsSection({ detail }) {
  if (!detail) return null;
  if (!detail.specialists?.length) {
    const fb = detail.specialist_fallback || detail.specialists_fallback;
    return (
      <section>
        <h3 className="text-sm font-semibold text-charcoal mb-2">Specialists</h3>
        <p className="text-sm text-forest-ink">
          {fb?.message || 'Individual clinician profiles are added only once confirmed against the hospital’s own directory.'}
        </p>
        {fb?.next_step && <p className="text-sm text-charcoal mt-2">{fb.next_step}</p>}
      </section>
    );
  }
  return (
    <section>
      <h3 className="text-sm font-semibold text-charcoal mb-2">Specialists</h3>
      <div className="space-y-2">
        {detail.specialists.map((s) => (
          <div key={s.name} className="rounded-2xl border border-hair bg-paper p-3">
            <p className="font-semibold text-forest-ink">{s.name}</p>
            <p className="text-sm text-charcoal">{s.role_title || s.title}</p>
            <p className="text-sm text-forest-ink">
              {[s.institution, s.city, s.country].filter(Boolean).join(', ')}
            </p>
            {s.source_url ? (
              <a
                href={s.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-forest-ink hover:text-forest-shadow"
              >
                <ExternalLink className="w-3 h-3" />
                Verified {s.verified_on || s.source_retrieved_at || ''}
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function SubtypeList({ subtypes, onSelect }) {
  if (!subtypes?.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-charcoal">Subtypes differ substantially in treatment and outlook:</p>
      {subtypes.map((s) => (
        <button
          key={s.name}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(s);
          }}
          className={`w-full text-left rounded-2xl border px-3 py-2 transition-colors ${
            s.urgency === 'EMERGENT'
              ? 'border-hair bg-[#f6eee9] hover:bg-[#f3e6de]'
              : 'border-hair bg-mist hover:bg-paper'
          }`}
        >
          <span className="flex items-center justify-between gap-2">
            <strong className="text-sm text-forest-ink">{s.name}</strong>
            {s.urgency === 'EMERGENT' && (
              <Badge className="shrink-0 text-[10px] px-1.5 py-0 bg-paper text-forest-ink border border-hair">
                Emergent
              </Badge>
            )}
          </span>
          {s.note && <span className="block text-xs text-charcoal/70 mt-1 leading-snug">{s.note}</span>}
        </button>
      ))}
    </div>
  );
}

function CancerCard({ cancer, toneKey, onOpen }) {
  const tone = CARD_TONES[toneKey] || CARD_TONES.common;
  const slug = cancer.slug || (cancer.name ? cancer.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : null);
  const detailUrl = slug ? `/rare-cancers/${slug}` : null;

  return (
    <Card className="bg-paper border border-hair rounded-3xl p-0 h-full flex flex-col overflow-hidden">
      <CardHeader className="flex-grow p-5 pb-3">
        <Badge className={`w-fit ${TAG_CLASS} mb-3`}>
          <span className="truncate block max-w-full">{cancer.type || tone.fallbackType}</span>
        </Badge>
        <CardTitle className="text-lg sm:text-xl text-forest-ink font-medium mb-3 leading-snug break-words" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
          {detailUrl ? (
            <Link to={detailUrl} className="hover:underline text-forest-ink">
              {cancer.name}
            </Link>
          ) : (
            cancer.name
          )}
        </CardTitle>
        {cancer.description && (
          <CardDescription className="text-[#3d4a42] text-sm leading-relaxed line-clamp-3 break-words">
            {cancer.description}
          </CardDescription>
        )}
        {cancer.who_it_affects && (
          <p className="mt-3 text-xs text-[#3d4a42] leading-relaxed line-clamp-2 break-words">
            {cancer.who_it_affects}
          </p>
        )}
        <SubtypeList
          subtypes={cancer.subtypes}
          onSelect={(sub) => onOpen({ ...cancer, name: sub.name })}
        />
      </CardHeader>
      <CardContent className="p-5 pt-0 mt-auto flex flex-col sm:flex-row gap-2">
        {detailUrl && (
          <Link
            to={detailUrl}
            className="rare-card-btn flex-1 inline-flex items-center justify-center bg-forest-ink hover:bg-forest-shadow text-cream-paper font-medium py-2.5 px-3 text-sm rounded-2xl transition"
          >
            <span className="truncate">Full Guide</span>
            <ArrowRight className="h-4 w-4 ml-1.5 flex-shrink-0" />
          </Link>
        )}
        <Button
          variant="outline"
          className="border-hair hover:bg-mist text-forest-ink text-xs py-2.5 px-3 rounded-2xl"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen(cancer);
          }}
        >
          Quick Preview
        </Button>
      </CardContent>
    </Card>
  );
}

function BulletList({ items }) {
  if (!items?.length) {
    return <p className="text-sm text-forest-ink italic">Not available</p>;
  }
  return (
    <ul className="list-disc pl-5 space-y-1 text-sm text-forest-ink leading-relaxed">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function RareCancersPage() {
  const navigate = useNavigate();
  const [rareCancers, setRareCancers] = useState(RARE_CANCERS_DATA);
  const [commonCancers, setCommonCancers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCancer, setSelectedCancer] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [userRegion, setUserRegion] = useState(regionFromProfile);
  const [catalogCounts, setCatalogCounts] = useState(null);

  // Fetch cancer types from backend
  const fetchRareCancers = useCallback(async () => {
    const apply = (data) => {
      const parsed = catalogLists(data);
      if (!parsed) return false;
      if (parsed.rare.length) setRareCancers(parsed.rare);
      if (parsed.common.length) setCommonCancers(parsed.common);
      if (parsed.counts) setCatalogCounts(parsed.counts);
      return true;
    };

    setError('');
    try {
      const response = await axios.get(`${API}/cancer-types`, { timeout: 15000 });
      if (apply(response.data)) return;
    } catch {
      // Keep the bundled catalog. An HTML shell or a failed request is not an empty list.
    }

    try {
      const rareResponse = await axios.get(`${API}/rare-cancers`, { timeout: 15000 });
      if (!apply(rareResponse.data)) {
        setRareCancers((prev) => (prev && prev.length > 0 ? prev : RARE_CANCERS_DATA));
      }
    } catch {
      setRareCancers((prev) => (prev && prev.length > 0 ? prev : RARE_CANCERS_DATA));
    }
  }, []);

  const fetchCancerDetail = useCallback(async (cancerName, region) => {
    if (!cancerName) return;
    setDetailLoading(true);
    setDetailError('');
    setDetail(null);
    try {
      const encoded = encodeURIComponent(cancerName);
      const res = await axios.get(`${API}/rare-cancers/${encoded}`, {
        params: region ? { region } : undefined,
        timeout: 60000,
      });
      setDetail(res.data);
    } catch (err) {
      console.error('Error fetching cancer detail:', err);
      if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        setDetailError('Cannot connect to backend. Please ensure the server is running.');
      } else if (err.response?.status === 404) {
        setDetailError('No clinical detail is listed for this cancer type yet.');
      } else {
        setDetailError('Unable to load details right now. Please try again.');
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetail = (cancer) => {
    if (!cancer?.name) return;
    setSelectedCancer(cancer);
    setDetailOpen(true);
    fetchCancerDetail(cancer.name, userRegion);
  };

  useEffect(() => {
    fetchRareCancers();
  }, [fetchRareCancers]);

  // Search functionality (client-side, using already-fetched data)
  const handleSearch = useCallback(
    (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      // Combine all cancers for search
      const allCancers = [...rareCancers, ...commonCancers];
      const q = query.toLowerCase().trim();
      
      
      const filtered = allCancers.filter(
        (cancer) => {
          const nameMatch = cancer.name?.toLowerCase().includes(q);
          const typeMatch = cancer.type?.toLowerCase().includes(q);
          const descMatch = cancer.description?.toLowerCase().includes(q);
          const categoryMatch = cancer.category?.toLowerCase().includes(q);
          const subtypeMatch = (cancer.subtypes || []).some(
            (s) => s.name?.toLowerCase().includes(q) || s.note?.toLowerCase().includes(q)
          );

          return nameMatch || typeMatch || descMatch || categoryMatch || subtypeMatch;
        }
      );

      setSearchResults(filtered);
      setIsSearching(false);
    },
    [rareCancers, commonCancers]
  );

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm) {
        handleSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, handleSearch]);

  const filterCancers = (cancers) => {
    if (!searchTerm) return cancers;
    const q = searchTerm.toLowerCase().trim();
    return cancers.filter(cancer => {
      const nameMatch = cancer.name?.toLowerCase().includes(q);
      const typeMatch = cancer.type?.toLowerCase().includes(q);
      const descMatch = cancer.description?.toLowerCase().includes(q);
      const categoryMatch = cancer.category?.toLowerCase().includes(q);
      const subtypeMatch = (cancer.subtypes || []).some(
        (s) => s.name?.toLowerCase().includes(q) || s.note?.toLowerCase().includes(q)
      );
      return nameMatch || typeMatch || descMatch || categoryMatch || subtypeMatch;
    });
  };

  const ultraRare = rareCancers.filter(c => c.category === 'ultra-rare');
  const veryRare = rareCancers.filter(c => c.category === 'very-rare');
  const rare = rareCancers.filter(c => c.category === 'rare');
  const listedRareTotal = Number(catalogCounts?.rare_total);
  const expertiseCount = Number.isFinite(listedRareTotal) && listedRareTotal > 0
    ? listedRareTotal
    : rareCancers.length;

  return (
    <>
    <style>{`
      .rare-segment [role="tab"] {
        min-width: 0 !important;
        min-height: 40px !important;
        padding: 0 10px !important;
        color: #0f3e17 !important;
        background: transparent !important;
        box-shadow: none !important;
      }
      .rare-segment [role="tab"][data-state="active"] {
        background: #ffffff !important;
        color: #0f3e17 !important;
        box-shadow: 0 1px 2px rgba(14, 17, 22, 0.08) !important;
      }
      .rare-card-btn {
        color: #fffefc !important;
        background: #0f3e17 !important;
      }
      .page-shell [data-radix-dialog-title],
      [data-radix-dialog-title] {
        font-family: "Instrument Sans", system-ui, sans-serif !important;
        color: #0f3e17 !important;
      }
    `}</style>
    <div className="min-h-screen bg-cream-paper pt-10 sm:pt-14 pb-8 sm:pb-12 px-4 sm:px-6">
      <MetaTags
        title="Rare & Ultra-Rare Cancer Directory"
        description="Explore 39 rare and ultra-rare cancer types with designated NCI treatment centers, specialist teams, and cutting-edge clinical pathways."
        path="/rare-cancers"
      />
      <BackToApp />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Badge className="mb-4 bg-mist text-forest-ink border-hair px-4 py-1.5 text-sm font-medium">
            <Activity className="h-3 w-3 mr-1.5 inline" />
            Specialized Care
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-forest-ink mb-4 px-2">
            Rare & Ultra-Rare Cancers
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-charcoal max-w-3xl mx-auto px-2 leading-relaxed">
            Expertise in {expertiseCount} rare and ultra-rare cancer types with access to specialist centres and cutting-edge treatments
          </p>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 max-w-2xl mx-auto"
          >
            <div className="bg-paper border border-hair rounded-2xl p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-forest-ink flex-shrink-0" />
              <p className="text-forest-ink text-sm">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Search */}
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="relative max-w-2xl mx-auto">
            <Search
              className="pointer-events-none"
              style={{
                position: 'absolute',
                left: 18,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 18,
                height: 18,
                color: '#3d4a42',
                zIndex: 1,
              }}
            />
            {isSearching && (
              <Loader2
                className="animate-spin pointer-events-none"
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 18,
                  height: 18,
                  color: '#0f3e17',
                  zIndex: 1,
                }}
              />
            )}
            <input
              type="text"
              placeholder="Search cancer types..."
              className="w-full h-12 bg-paper border border-hair rounded-2xl text-forest-ink placeholder:text-[#3d4a42] focus:border-forest-ink focus:ring-0 focus:outline-none text-sm sm:text-base"
              style={{ paddingLeft: 48, paddingRight: 48 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {searchTerm && (
            <p className="text-center text-charcoal text-sm mt-4 mb-0">
              {isSearching ? 'Searching...' : searchResults.length > 0 
                ? `Found ${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`
                : 'No results found'}
            </p>
          )}
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="ultra-rare" className="w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mb-8"
          >
            <TabsList className="rare-segment h-auto min-h-11 w-full grid grid-cols-2 sm:grid-cols-4 gap-1 rounded-2xl bg-mist p-1 overflow-hidden border border-hair">
              <TabsTrigger value="ultra-rare" className="h-10 min-w-0 rounded-xl px-2 text-xs sm:text-sm font-medium">
                Ultra-Rare ({ultraRare.length})
              </TabsTrigger>
              <TabsTrigger value="very-rare" className="h-10 min-w-0 rounded-xl px-2 text-xs sm:text-sm font-medium">
                Very Rare ({veryRare.length})
              </TabsTrigger>
              <TabsTrigger value="rare" className="h-10 min-w-0 rounded-xl px-2 text-xs sm:text-sm font-medium">
                Rare ({rare.length})
              </TabsTrigger>
              <TabsTrigger value="common" className="h-10 min-w-0 rounded-xl px-2 text-xs sm:text-sm font-medium">
                Common ({commonCancers.length})
              </TabsTrigger>
            </TabsList>
          </motion.div>

          {/* Ultra-Rare Tab */}
          <TabsContent value="ultra-rare" className="mt-0">
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {filterCancers(ultraRare).length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-charcoal text-lg">No ultra-rare cancers found.</p>
                </div>
              ) : (
                filterCancers(ultraRare).map((cancer, idx) => (
                  <motion.div
                    key={cancer.id || idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.5 }}
                    whileHover={{ scale: 1.02, y: -3 }}
                    className="h-full"
                  >
                    <CancerCard cancer={cancer} toneKey="ultra-rare" onOpen={openDetail} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabsContent>

          {/* Very Rare Tab */}
          <TabsContent value="very-rare" className="mt-0">
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {filterCancers(veryRare).length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-charcoal text-lg">No very rare cancers found.</p>
                </div>
              ) : (
                filterCancers(veryRare).map((cancer, idx) => (
                  <motion.div
                    key={cancer.id || idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.5 }}
                    whileHover={{ scale: 1.02, y: -3 }}
                    className="h-full"
                  >
                    <CancerCard cancer={cancer} toneKey="very-rare" onOpen={openDetail} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabsContent>

          {/* Rare Tab */}
          <TabsContent value="rare" className="mt-0">
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {filterCancers(rare).length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-charcoal text-lg">No rare cancers found.</p>
                </div>
              ) : (
                filterCancers(rare).map((cancer, idx) => (
                  <motion.div
                    key={cancer.id || idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.5 }}
                    whileHover={{ scale: 1.02, y: -3 }}
                    className="h-full"
                  >
                    <CancerCard cancer={cancer} toneKey="rare" onOpen={openDetail} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabsContent>

          {/* Common Tab */}
          <TabsContent value="common" className="mt-0">
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {filterCancers(commonCancers).length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-charcoal text-lg">No common cancers found.</p>
                </div>
              ) : (
                filterCancers(commonCancers).map((cancer, idx) => (
                  <motion.div
                    key={cancer.id || idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.5 }}
                    whileHover={{ scale: 1.02, y: -3 }}
                    className="h-full"
                  >
                    <CancerCard cancer={cancer} toneKey="common" onOpen={openDetail} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={detailOpen} onOpenChange={(open) => {
        setDetailOpen(open);
        if (!open) {
          setSelectedCancer(null);
          setDetail(null);
          setDetailError('');
        }
      }}>
        <DialogContent
          className="bg-paper border-hair text-forest-ink w-[95vw] sm:w-full max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col p-0 rounded-3xl mx-2 sm:mx-4"
          style={{ zIndex: 9999 }}
        >
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-hair">
            <div className="rounded-2xl border border-hair bg-mist p-3 mb-3 text-sm text-forest-ink leading-relaxed">
              This information has not yet been reviewed by a practising oncologist.
              It is general information, not medical advice.
            </div>
            <DialogTitle className="text-xl sm:text-2xl font-medium text-forest-ink mb-2 break-words" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
              {selectedCancer?.name || 'Cancer details'}
            </DialogTitle>
            <DialogDescription className="text-[#3d4a42] text-xs sm:text-sm leading-relaxed">
              General information, not medical advice. Treatment decisions require an oncologist who has seen your case.
            </DialogDescription>
            <div className="pt-3">
              <label className="text-xs text-forest-ink mr-2">Show centres for</label>
              <select
                value={userRegion}
                onChange={(e) => {
                  const next = e.target.value;
                  setUserRegion(next);
                  if (selectedCancer?.name) fetchCancerDetail(selectedCancer.name, next);
                }}
                className="bg-paper border border-hair text-forest-ink text-sm rounded-2xl px-3 py-2"
              >
                {REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
            {detailLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-forest-ink" />
                <p className="text-charcoal text-sm">Loading details…</p>
              </div>
            ) : detailError ? (
              <div className="flex items-start gap-3 rounded-2xl border border-hair bg-mist p-4">
                <AlertCircle className="h-5 w-5 text-forest-ink mt-0.5 flex-shrink-0" />
                <p className="text-sm text-forest-ink">{detailError}</p>
              </div>
            ) : detail ? (
              <>
                {detail.red_flags?.length > 0 && (
                  <div className="rounded-2xl border border-hair bg-[#f6eee9] p-3 sm:p-4">
                    <p className="text-sm font-semibold text-forest-ink mb-2">Seek medical attention promptly</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-forest-ink">
                      {detail.red_flags.map((f) => <li key={f}>{f}</li>)}
                    </ul>
                    {detail.review_scope === 'RED_FLAGS_ONLY' && (
                      <p className="text-xs text-[#3d4a42] mt-3 leading-relaxed">
                        These urgent-action lines have not been signed off by a practising
                        oncologist. If you have these symptoms, seek emergency care now.
                      </p>
                    )}
                  </div>
                )}

                <TrialsSection detail={detail} />

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">About</h3>
                  <p className="text-sm text-forest-ink leading-relaxed">{detail.description || 'Not available'}</p>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">Symptoms</h3>
                  <BulletList items={detail.symptoms} />
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">Treatment options</h3>
                  <BulletList items={detail.treatment_options} />
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">Outlook</h3>
                  <p className="text-sm text-forest-ink leading-relaxed">{detail.prognosis || 'Not available'}</p>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">Research</h3>
                  <p className="text-sm text-forest-ink leading-relaxed">{detail.research_status || 'Not available'}</p>
                </section>

                {detail.molecular_markers?.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-charcoal mb-1">Key tests</h3>
                    <p className="text-xs text-forest-ink mb-2">Tests that establish the diagnosis and guide treatment choice</p>
                    <div className="flex flex-wrap gap-2">
                      {detail.molecular_markers.map((m) => (
                        <Badge key={m} className={TAG_CLASS}>{m}</Badge>
                      ))}
                    </div>
                  </section>
                )}

                {detail.india_context && userRegion === 'India' && (
                  <section>
                    <h3 className="text-sm font-semibold text-charcoal mb-2">In India</h3>
                    <p className="text-sm text-forest-ink leading-relaxed">{detail.india_context}</p>
                  </section>
                )}

                <section>
                  <h3 className="text-sm font-semibold text-charcoal mb-2">Where this is treated</h3>
                  <CenterList centers={detail.specialized_centers} />
                </section>

                <SpecialistsSection detail={detail} />

                <p className="text-xs text-[#3d4a42] leading-relaxed border-t border-hair pt-4">
                  Reviewed {detail.last_reviewed || 'date not listed'}. General information, not medical advice.
                  Treatment decisions require an oncologist who has seen your case.
                </p>
              </>
            ) : null}
          </div>

          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-hair flex justify-end">
            <Button
              variant="outline"
              className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium border-hair text-forest-ink hover:bg-mist rounded-2xl w-full sm:w-auto"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDetailOpen(false);
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
